import React, { useMemo, useState, useEffect, useRef } from "react";
import { testimonials } from "./siteUtils";
import useMediaQuery from "@mui/material/useMediaQuery";
import "./App.css";
import TestimonialCard from "./components/TestimonialCard";
import KeyboardArrowLeftRoundedIcon from "@mui/icons-material/KeyboardArrowLeftRounded";
import KeyboardArrowRightRoundedIcon from "@mui/icons-material/KeyboardArrowRightRounded";

const chunkTestimonials = (items, size) => {
  const chunkSize = Math.max(1, size);

  if (!items.length) {
    return [];
  }

  const chunks = [];

  for (let i = 0; i < items.length; i += chunkSize) {
    chunks.push(items.slice(i, i + chunkSize));
  }

  const lastChunk = chunks[chunks.length - 1];

  if (lastChunk && lastChunk.length < chunkSize) {
    const filledChunk = [...lastChunk];
    let pointer = 0;

    while (filledChunk.length < chunkSize) {
      filledChunk.push(items[pointer % items.length]);
      pointer += 1;
    }

    chunks[chunks.length - 1] = filledChunk;
  }

  return chunks;
};

export const ReviewCarousel = () => {
  const isMobile = useMediaQuery("(max-width: 1100px)");
  const cardsPerSlide = isMobile ? 1 : 3;

  const baseSlides = useMemo(
    () => chunkTestimonials(testimonials, cardsPerSlide),
    [cardsPerSlide]
  );

  const hasLoop = !isMobile && baseSlides.length > 1;

  const extendedSlides = useMemo(() => {
    if (!hasLoop) {
      return baseSlides;
    }

    const clones = [];

    for (let copy = 0; copy < 3; copy += 1) {
      baseSlides.forEach((group, index) => {
        clones.push({
          group,
          cloneIndex: copy,
          baseIndex: index,
        });
      });
    }

    return clones;
  }, [baseSlides, hasLoop]);

  const initialIndex = hasLoop ? baseSlides.length : 0;

  const [activeIndex, setActiveIndex] = useState(0);
  const [mobileVisibleCount, setMobileVisibleCount] = useState(
    Math.min(3, testimonials.length)
  );
  const [isInstant, setIsInstant] = useState(false);

  const activeIndexRef = useRef(activeIndex);

  useEffect(() => {
    activeIndexRef.current = activeIndex;
  }, [activeIndex]);

  useEffect(() => {
    setIsInstant(true);
    setActiveIndex(initialIndex);

    const id = requestAnimationFrame(() => setIsInstant(false));

    return () => cancelAnimationFrame(id);
  }, [initialIndex]);

  useEffect(() => {
    if (isMobile) {
      setMobileVisibleCount(Math.min(3, testimonials.length));
    }
  }, [isMobile]);

  const goTo = (index) => {
    if (!baseSlides.length) {
      return;
    }

    if (hasLoop) {
      setActiveIndex(baseSlides.length + index);
      return;
    }

    setActiveIndex(() => {
      const clamped = Math.min(Math.max(index, 0), baseSlides.length - 1);
      return Number.isNaN(clamped) ? 0 : clamped;
    });
  };

  const handlePrev = () => {
    if (!baseSlides.length) {
      return;
    }

    if (hasLoop) {
      setActiveIndex((prev) => prev - 1);
    } else {
      setActiveIndex((prev) => Math.max(prev - 1, 0));
    }
  };

  const handleNext = () => {
    if (!baseSlides.length) {
      return;
    }

    if (hasLoop) {
      setActiveIndex((prev) => prev + 1);
    } else {
      setActiveIndex((prev) => Math.min(prev + 1, baseSlides.length - 1));
    }
  };

  const canNavigate = baseSlides.length > 1;
  const showNavigation = canNavigate && !isMobile;

  const dotActiveIndex = baseSlides.length
    ? ((activeIndex % baseSlides.length) + baseSlides.length) % baseSlides.length
    : 0;

  const handleTrackTransitionEnd = () => {
    if (!hasLoop) {
      return;
    }

    const total = baseSlides.length;
    const current = activeIndexRef.current;

    if (current >= total * 2) {
      setIsInstant(true);
      setActiveIndex(current - total);
      return;
    }

    if (current < total) {
      setIsInstant(true);
      setActiveIndex(current + total);
    }
  };

  useEffect(() => {
    if (!isInstant) {
      return;
    }

    const id = requestAnimationFrame(() => {
      setIsInstant(false);
    });

    return () => cancelAnimationFrame(id);
  }, [isInstant]);

  if (isMobile) {
    const visibleTestimonials = testimonials.slice(0, mobileVisibleCount);

    return (
      <div className="testimonial-stack">
        <div className="stack-grid">
          {visibleTestimonials.map(({ desc, author }, index) => (
            <div key={`${author}-${index}`} className="stack-item">
              <TestimonialCard name={author} testimonial={desc} stacked />
            </div>
          ))}
        </div>

        {mobileVisibleCount < testimonials.length && (
          <button
            type="button"
            className="stack-show-more"
            onClick={() =>
              setMobileVisibleCount((prev) =>
                Math.min(prev + 3, testimonials.length)
              )
            }
          >
            Show more
          </button>
        )}
      </div>
    );
  }

  return (
    <div className="carousel-wrapper">
      {showNavigation && (
        <button
          type="button"
          className="carousel-arrow prev"
          onClick={handlePrev}
          disabled={!canNavigate}
          aria-label="Show previous testimonials"
        >
          <KeyboardArrowLeftRoundedIcon />
        </button>
      )}

      <div className="carousel-window">
        <div
          className="carousel-track"
          style={{
            transform: `translateX(-${activeIndex * 100}%)`,
            transition: isInstant ? "none" : "transform 450ms ease",
          }}
          onTransitionEnd={handleTrackTransitionEnd}
        >
          {extendedSlides.map((slideEntry, slideIndex) => {
            const slideGroup = hasLoop ? slideEntry.group : slideEntry;
            const keySuffix = hasLoop
              ? `${slideEntry.cloneIndex}-${slideEntry.baseIndex}`
              : slideIndex;

            return (
              <div
                className="carousel-slide"
                key={`slide-${keySuffix}-${slideIndex}`}
              >
                <div
                  className="slide-grid"
                  style={{ "--columns": slideGroup.length }}
                >
                  {slideGroup.map(({ desc, author }, cardIndex) => (
                    <div
                      className="grid-cell"
                      key={`${keySuffix}-${cardIndex}-${author}`}
                    >
                      <TestimonialCard name={author} testimonial={desc} />
                    </div>
                  ))}
                </div>
              </div>
            );
          })}
        </div>
      </div>

      {showNavigation && (
        <button
          type="button"
          className="carousel-arrow next"
          onClick={handleNext}
          disabled={!canNavigate}
          aria-label="Show more testimonials"
        >
          <KeyboardArrowRightRoundedIcon />
        </button>
      )}

      {canNavigate && (
        <div className="carousel-dots">
          {baseSlides.map((_, dotIndex) => (
            <button
              key={`dot-${dotIndex}`}
              type="button"
              className={dotIndex === dotActiveIndex ? "active" : ""}
              aria-label={`Jump to testimonial set ${dotIndex + 1}`}
              onClick={() => goTo(dotIndex)}
            />
          ))}
        </div>
      )}
    </div>
  );
};
